import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Scale, Phone, MessageSquareText, Loader2, ArrowRight, ShieldCheck, Play } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ENROLL_URL = "https://myeliteassociates.com/join/132404534/";
const PHONE_DISPLAY = "214-500-4192";
const PHONE_TEL = "+12145004192";
const VIDEO_EMBED = "https://www.youtube-nocookie.com/embed/fZyWF5qHPEE?rel=0&modestbranding=1";

const EASE = [0.22, 1, 0.36, 1];

export default function FunnelPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [contact, setContact] = useState("text");
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [src, setSrc] = useState("");

  useEffect(() => {
    const s = new URLSearchParams(window.location.search).get("src") || "";
    setSrc(s);
    if (!sessionStorage.getItem("mll_tracked")) {
      sessionStorage.setItem("mll_tracked", "1");
      const referrer = s ? `src:${s}` : document.referrer;
      fetch(`${API}/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path: "/", referrer, event: "visit" }),
      }).catch(() => {});
    }
  }, []);

  const track = (event) =>
    fetch(`${API}/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path: "/", referrer: "", event }),
    }).catch(() => {});

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        interest: "partner",
        preferred_contact: contact,
        message: `Video funnel lead${src ? ` via src:${src}` : ""}`,
        consent: true,
      });
      setStep(2);
      window.scrollTo(0, 0);
    } catch {
      toast.error("Something went wrong — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-[#0B192C] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-colors duration-200";

  return (
    <main className="min-h-screen bg-[#0B192C] grain-overlay relative" data-testid="funnel-page">
      <div className="absolute inset-0 hero-spotlight" />
      <div className="relative max-w-xl mx-auto px-4 sm:px-6 py-10 md:py-16">
        <div className="flex items-center justify-center gap-3 mb-10">
          <span className="w-9 h-9 rounded-lg bg-sky-400 text-[#0B192C] flex items-center justify-center">
            <Scale size={18} strokeWidth={2.2} />
          </span>
          <span className="leading-none">
            <span className="font-display font-extrabold text-lg tracking-tight text-white block">John Jones</span>
            <span className="font-mono-accent text-[10px] uppercase tracking-[0.22em] text-sky-400 block mt-1">Independent Associate</span>
          </span>
        </div>

        <AnimatePresence mode="wait">
          {step === 1 ? (
            <motion.div key="step1" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.5, ease: EASE }}>
              <h1 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-white text-center leading-[1.12] mb-5" data-testid="funnel-headline">
                No products. No inventory.<br />No pitching pills or powders.
              </h1>
              <p className="text-slate-300 text-center leading-relaxed mb-8 max-w-md mx-auto" data-testid="funnel-subheadline">
                I almost said no to this. It's a real system with real mentorship — and the part
                everyone asks about first is how you actually get paid. Drop your details and watch
                the short overview — then decide for yourself.
              </p>

              <div className="bg-white rounded-3xl p-7 md:p-8 shadow-2xl shadow-blue-950/40" data-testid="funnel-form-card">
                <form onSubmit={submit} className="space-y-4" data-testid="funnel-form">
                  <input data-testid="funnel-name-input" required minLength={2} placeholder="First and last name" value={form.name} onChange={set("name")} className={inputCls} />
                  <input data-testid="funnel-email-input" required type="email" placeholder="Email address" value={form.email} onChange={set("email")} className={inputCls} />
                  <input data-testid="funnel-phone-input" required minLength={7} type="tel" placeholder="Cell phone" value={form.phone} onChange={set("phone")} className={inputCls} />
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Preferred contact method</p>
                    <div className="grid grid-cols-2 gap-2" data-testid="funnel-contact-group">
                      {[{ v: "text", label: "Text me" }, { v: "call", label: "Call me" }].map((o) => (
                        <button
                          key={o.v}
                          type="button"
                          data-testid={`funnel-contact-${o.v}`}
                          onClick={() => setContact(o.v)}
                          className={`py-2.5 rounded-xl text-sm font-semibold border transition-colors duration-200 ${
                            contact === o.v
                              ? "bg-[#1D4ED8] border-[#1D4ED8] text-white"
                              : "border-slate-200 text-slate-600 hover:border-blue-400 hover:text-blue-700"
                          }`}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <label className="flex items-start gap-3 text-[11px] text-slate-500 leading-relaxed cursor-pointer select-none" data-testid="funnel-consent-label">
                    <input data-testid="funnel-consent-checkbox" required type="checkbox" className="mt-0.5 w-4 h-4 shrink-0 rounded border-slate-300 accent-blue-600" />
                    <span>I agree that John Jones / Elite Associates may contact me by my selected method about the LegalShield business opportunity I requested. Consent isn’t a condition of purchase. For texts, message and data rates may apply; reply STOP to opt out.</span>
                  </label>
                  <button
                    data-testid="funnel-submit-button"
                    type="submit"
                    disabled={submitting}
                    className="group w-full flex items-center justify-center gap-2 bg-[#1D4ED8] hover:bg-blue-800 disabled:opacity-60 text-white font-semibold py-4 rounded-xl transition-colors duration-300"
                  >
                    {submitting ? <Loader2 size={18} className="animate-spin" /> : <Play size={16} className="transition-transform duration-300 group-hover:scale-110" />}
                    {submitting ? "One moment..." : "Show me how it works"}
                  </button>
                </form>
                <p className="text-[11px] text-slate-400 text-center mt-4 flex items-center justify-center gap-1.5">
                  <ShieldCheck size={12} className="text-blue-500" />
                  Your info goes to me personally — no spam, no pressure, ever.
                </p>
              </div>
            </motion.div>
          ) : (
            <motion.div key="step2" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} data-testid="funnel-video-step">
              <h1 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-white text-center leading-[1.12] mb-3">
                Thanks, {form.name.split(" ")[0]} — here's the overview.
              </h1>
              <p className="text-slate-300 text-center leading-relaxed mb-8 max-w-md mx-auto">
                Watch this first. Then choose your next step below — enroll online in minutes, dig
                into the full details, or reach me directly with any question.
              </p>

              <div className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-blue-950/50 mb-10" data-testid="funnel-video">
                <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                  <iframe
                    src={VIDEO_EMBED}
                    title="Business overview"
                    className="absolute inset-0 w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>

              <div className="space-y-4">
                <a
                  data-testid="enroll-button"
                  href={ENROLL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track("enroll_click")}
                  className="group flex items-center justify-between w-full bg-sky-400 hover:bg-sky-300 text-[#0B192C] rounded-2xl p-6 transition-colors duration-300"
                >
                  <span>
                    <span className="font-display font-extrabold text-xl block">Ready? Enroll now</span>
                    <span className="text-sm text-[#0B192C]/70">Takes a few minutes on the official enrollment page</span>
                  </span>
                  <ArrowRight size={22} className="transition-transform duration-300 group-hover:translate-x-1.5 shrink-0" />
                </a>

                <a
                  data-testid="more-info-button"
                  href="/info#videos"
                  onClick={() => track("more_info_click")}
                  className="group flex items-center justify-between w-full border border-sky-400/50 bg-white/[0.04] hover:bg-white/[0.1] text-white rounded-2xl p-6 transition-colors duration-300"
                >
                  <span>
                    <span className="font-display font-bold text-lg block">Want more details first?</span>
                    <span className="text-sm text-slate-400"><span className="text-sky-400 font-semibold">Click here</span> for the full 22-min presentation, real prices &amp; associate stories — no forms</span>
                  </span>
                  <ArrowRight size={20} className="text-sky-400 transition-transform duration-300 group-hover:translate-x-1.5 shrink-0" />
                </a>

                <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-6" data-testid="contact-card">
                  <div className="flex items-center gap-4 mb-5">
                    <img src="/john-jones.png" alt="John Jones" className="w-14 h-14 rounded-full object-cover border-2 border-sky-400/60" />
                    <div>
                      <p className="font-display font-bold text-white">Got questions?</p>
                      <p className="text-slate-400 text-sm">Call or text John directly — {PHONE_DISPLAY}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <a
                      data-testid="call-button"
                      href={`tel:${PHONE_TEL}`}
                      onClick={() => track("call_click")}
                      className="flex items-center justify-center gap-2 border border-sky-400/50 text-sky-300 hover:bg-sky-400 hover:text-[#0B192C] font-semibold py-3 rounded-xl transition-colors duration-200"
                    >
                      <Phone size={16} /> Call
                    </a>
                    <a
                      data-testid="text-button"
                      href={`sms:${PHONE_TEL}`}
                      onClick={() => track("call_click")}
                      className="flex items-center justify-center gap-2 border border-sky-400/50 text-sky-300 hover:bg-sky-400 hover:text-[#0B192C] font-semibold py-3 rounded-xl transition-colors duration-200"
                    >
                      <MessageSquareText size={16} /> Text
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <p className="text-[11px] text-slate-500 leading-relaxed text-center mt-12 max-w-md mx-auto" data-testid="funnel-disclaimer">
          Operated by John Jones, an independent associate. This is a real business, not a shortcut
          to riches — associates earn commissions on memberships sold and renewed, results vary with
          effort, and no income is promised or guaranteed.
        </p>
      </div>
    </main>
  );
}
